"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import Button from "@/components/ui/Button";

interface HeroProps {
  title: string;
  subtitle: string;
}

export default function Hero({ title, subtitle }: HeroProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/busca?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <section className="relative bg-linear-to-br from-mygreen via-mygreen to-mygreen/90 overflow-hidden pb-16 md:pb-24">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-96 h-96 bg-myyellow rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10 py-12 md:pt-20 md:pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-2">
              <MapPinIcon className="w-4 h-4 text-white" />
              <span className="text-sm font-medium text-white">
                Santa Fé do Sul, SP
              </span>
            </div>
          </div>

          {/* Main content */}
          <div className="text-center space-y-6 mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {title}
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>

          {/* Search bar */}
          <form
            onSubmit={handleSearch}
            className="bg-white rounded-2xl shadow-2xl p-2 max-w-3xl mx-auto mb-8"
          >
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-xl">
                <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Busque por empresas, serviços ou eventos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-gray-700 placeholder-gray-400"
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                className="md:w-auto whitespace-nowrap px-8"
              >
                Buscar
              </Button>
            </div>
          </form>

          {/* Quick links */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-sm text-white/80">Explorar:</span>
            <Link
              href="/categorias"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm font-medium text-white transition-all"
            >
              <i className="fa-solid fa-th-large text-xs"></i>
              Categorias
            </Link>
            <Link
              href="/eventos"
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm font-medium text-white transition-all"
            >
              <i className="fa-solid fa-calendar-days text-xs"></i>
              Eventos
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom curve - CSS approach for better responsiveness */}
      <div className="absolute bottom-0 left-0 right-0 h-12 md:h-16 lg:h-20 bg-white rounded-t-[50%_100%]"></div>
    </section>
  );
}
